/**
 * Sprites pixel-art (estilo Habbo, arte própria) desenhados em <canvas>.
 * Validados via render headless. Coordenadas: (ox,oy) = base/pés no CENTRO do tile.
 * px(): bloco "pixel" alinhado; faces iso via path.
 */

export const TW = 32; // largura do tile
export const TH = 16; // altura do tile
export const TD = 7; // profundidade 3D do tile

type Ctx = CanvasRenderingContext2D;

const rect = (ctx: Ctx, color: string, xx: number, yy: number, w: number, h: number) => {
  ctx.fillStyle = color;
  ctx.fillRect(Math.round(xx), Math.round(yy), w, h);
};

/** Tile do piso (topo losango + 2 faces de profundidade). (px,py) = vértice de topo. */
export function drawTile(ctx: Ctx, px: number, py: number, top: string, fl: string, fr: string) {
  ctx.fillStyle = top;
  ctx.beginPath();
  ctx.moveTo(px, py);
  ctx.lineTo(px + TW / 2, py + TH / 2);
  ctx.lineTo(px, py + TH);
  ctx.lineTo(px - TW / 2, py + TH / 2);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = 'rgba(40,70,110,0.16)';
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.fillStyle = fl;
  ctx.beginPath();
  ctx.moveTo(px - TW / 2, py + TH / 2);
  ctx.lineTo(px, py + TH);
  ctx.lineTo(px, py + TH + TD);
  ctx.lineTo(px - TW / 2, py + TH / 2 + TD);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = fr;
  ctx.beginPath();
  ctx.moveTo(px, py + TH);
  ctx.lineTo(px + TW / 2, py + TH / 2);
  ctx.lineTo(px + TW / 2, py + TH / 2 + TD);
  ctx.lineTo(px, py + TH + TD);
  ctx.closePath();
  ctx.fill();
}

export const WALL_H = 54;

/** Paredes do fundo (2 quads sobem reto do canto de trás) + trim. A/B/C = pontos
 * de tela: A=top(0,0) canto fundo, B=top(cols,0) direita, C=top(0,rows) esquerda. */
export function drawWalls(
  ctx: Ctx,
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number,
) {
  const h = WALL_H;
  const quad = (
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    x3: number,
    y3: number,
    x4: number,
    y4: number,
  ) => {
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.lineTo(x3, y3);
    ctx.lineTo(x4, y4);
    ctx.closePath();
    ctx.fill();
  };
  ctx.fillStyle = '#D6E1EE';
  quad(ax, ay, bx, by, bx, by - h, ax, ay - h);
  ctx.fillStyle = '#EAF1F9';
  quad(ax, ay - h, bx, by - h, bx, by - h - 4, ax, ay - h - 4);
  ctx.fillStyle = '#C2D0E0';
  quad(ax, ay, cx, cy, cx, cy - h, ax, ay - h);
  ctx.fillStyle = '#D6E0EE';
  quad(ax, ay - h, cx, cy - h, cx, cy - h - 4, ax, ay - h - 4);
}

export interface PersonStyle {
  skin: string;
  shirt: string;
  hair: string;
  fem: boolean;
}

/** Personagem chunky estilo Habbo. (ox,oy) = pés no centro do tile. */
export function drawPerson(ctx: Ctx, ox0: number, oy0: number, s: PersonStyle) {
  const ox = Math.round(ox0);
  const oy = Math.round(oy0);
  ctx.fillStyle = 'rgba(20,40,70,0.22)';
  ctx.beginPath();
  ctx.ellipse(ox, oy + 1, 9, 4, 0, 0, 7);
  ctx.fill();
  rect(ctx, '#2C3A52', ox - 5, oy - 9, 4, 9);
  rect(ctx, '#243149', ox + 1, oy - 9, 4, 9);
  rect(ctx, '#1A222F', ox - 5, oy - 1, 4, 2);
  rect(ctx, '#1A222F', ox + 1, oy - 1, 4, 2);
  rect(ctx, s.shirt, ox - 7, oy - 22, 14, 14);
  rect(ctx, 'rgba(0,0,0,0.12)', ox + 1, oy - 22, 6, 14);
  rect(ctx, s.shirt, ox - 9, oy - 21, 3, 10);
  rect(ctx, s.shirt, ox + 6, oy - 21, 3, 10);
  rect(ctx, s.skin, ox - 9, oy - 12, 3, 3);
  rect(ctx, s.skin, ox + 6, oy - 12, 3, 3);
  rect(ctx, s.skin, ox - 2, oy - 24, 4, 3);
  rect(ctx, s.skin, ox - 8, oy - 37, 16, 15);
  rect(ctx, 'rgba(0,0,0,0.10)', ox + 2, oy - 37, 6, 15);
  if (s.fem) {
    rect(ctx, s.hair, ox - 9, oy - 39, 18, 7);
    rect(ctx, s.hair, ox - 9, oy - 34, 3, 9);
    rect(ctx, s.hair, ox + 6, oy - 34, 3, 9);
  } else {
    rect(ctx, s.hair, ox - 8, oy - 39, 16, 6);
    rect(ctx, s.hair, ox - 8, oy - 35, 2, 3);
    rect(ctx, s.hair, ox + 6, oy - 35, 2, 3);
  }
  rect(ctx, '#2A2A2A', ox - 4, oy - 30, 2, 2);
  rect(ctx, '#2A2A2A', ox + 2, oy - 30, 2, 2);
  rect(ctx, '#B5746A', ox - 1, oy - 26, 3, 1);
  rect(ctx, 'rgba(255,255,255,0.16)', ox - 6, oy - 35, 3, 3);
}

/** Mesa pixel (iso box + monitor na cor `scr`). (ox,oy) = centro do tile. */
export function drawDesk(ctx: Ctx, ox0: number, oy0: number, scr: string) {
  const ox = Math.round(ox0);
  const oy = Math.round(oy0);
  const hw = 13;
  const hd = 6;
  const H = 9;
  ctx.fillStyle = 'rgba(20,40,70,0.20)';
  ctx.beginPath();
  ctx.ellipse(ox, oy + 1, hw, hd * 0.9, 0, 0, 7);
  ctx.fill();
  ctx.fillStyle = '#9AA6B4';
  ctx.beginPath();
  ctx.moveTo(ox - hw, oy - H);
  ctx.lineTo(ox, oy - H + hd);
  ctx.lineTo(ox, oy + hd);
  ctx.lineTo(ox - hw, oy);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#838F9E';
  ctx.beginPath();
  ctx.moveTo(ox + hw, oy - H);
  ctx.lineTo(ox, oy - H + hd);
  ctx.lineTo(ox, oy + hd);
  ctx.lineTo(ox + hw, oy);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#EDF1F6';
  ctx.beginPath();
  ctx.moveTo(ox, oy - H - hd);
  ctx.lineTo(ox + hw, oy - H);
  ctx.lineTo(ox, oy - H + hd);
  ctx.lineTo(ox - hw, oy - H);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = 'rgba(40,70,110,0.15)';
  ctx.lineWidth = 1;
  ctx.stroke();
  rect(ctx, '#2B3340', ox - 6, oy - H - 13, 12, 8);
  rect(ctx, scr, ox - 5, oy - H - 12, 10, 5);
  rect(ctx, 'rgba(255,255,255,0.20)', ox - 5, oy - H - 12, 10, 2);
  rect(ctx, '#3A4452', ox - 1, oy - H - 5, 2, 3);
}

/** Máquina de costura pixel (têxtil). (ox,oy) = centro do tile; `fab` = cor do tecido. */
export function drawSewing(ctx: Ctx, ox0: number, oy0: number, fab: string) {
  const ox = Math.round(ox0);
  const oy = Math.round(oy0);
  const hw = 13;
  const hd = 6;
  const H = 8;
  ctx.fillStyle = 'rgba(20,40,70,0.20)';
  ctx.beginPath();
  ctx.ellipse(ox, oy + 1, hw, hd * 0.9, 0, 0, 7);
  ctx.fill();
  ctx.fillStyle = '#B07A45';
  ctx.beginPath();
  ctx.moveTo(ox - hw, oy - H);
  ctx.lineTo(ox, oy - H + hd);
  ctx.lineTo(ox, oy + hd);
  ctx.lineTo(ox - hw, oy);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#9A6637';
  ctx.beginPath();
  ctx.moveTo(ox + hw, oy - H);
  ctx.lineTo(ox, oy - H + hd);
  ctx.lineTo(ox, oy + hd);
  ctx.lineTo(ox + hw, oy);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#D7A86A';
  ctx.beginPath();
  ctx.moveTo(ox, oy - H - hd);
  ctx.lineTo(ox + hw, oy - H);
  ctx.lineTo(ox, oy - H + hd);
  ctx.lineTo(ox - hw, oy - H);
  ctx.closePath();
  ctx.fill();
  rect(ctx, '#ECECEC', ox - 9, oy - H - 7, 17, 5);
  rect(ctx, '#CFCFCF', ox - 9, oy - H - 2, 17, 2);
  rect(ctx, '#EDEDED', ox + 4, oy - H - 23, 5, 16);
  rect(ctx, '#EDEDED', ox - 8, oy - H - 23, 17, 5);
  rect(ctx, '#DCDCDC', ox - 8, oy - H - 18, 4, 5);
  rect(ctx, '#6E7886', ox - 7, oy - H - 13, 2, 4);
  ctx.fillStyle = '#9AA6B4';
  ctx.beginPath();
  ctx.arc(ox + 8, oy - H - 14, 3, 0, 7);
  ctx.fill();
  rect(ctx, '#9AA6B4', ox - 1, oy - H - 29, 2, 6);
  rect(ctx, fab, ox - 4, oy - H - 29, 8, 3);
  rect(ctx, fab, ox - 9, oy - H - 1, 10, 4);
  rect(ctx, 'rgba(255,255,255,0.18)', ox - 9, oy - H - 1, 10, 1);
}

/** Mesa de corte pixel (têxtil, larga). (ox,oy) = centro do tile. */
export function drawCutTable(ctx: Ctx, ox0: number, oy0: number) {
  const ox = Math.round(ox0);
  const oy = Math.round(oy0);
  const hw = 22;
  const hd = 8;
  const H = 10;
  ctx.fillStyle = 'rgba(20,40,70,0.20)';
  ctx.beginPath();
  ctx.ellipse(ox, oy + 1, hw, hd * 0.9, 0, 0, 7);
  ctx.fill();
  ctx.fillStyle = '#AEB6C2';
  ctx.beginPath();
  ctx.moveTo(ox - hw, oy - H);
  ctx.lineTo(ox, oy - H + hd);
  ctx.lineTo(ox, oy + hd);
  ctx.lineTo(ox - hw, oy);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#97A0AD';
  ctx.beginPath();
  ctx.moveTo(ox + hw, oy - H);
  ctx.lineTo(ox, oy - H + hd);
  ctx.lineTo(ox, oy + hd);
  ctx.lineTo(ox + hw, oy);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#EFF3F8';
  ctx.beginPath();
  ctx.moveTo(ox, oy - H - hd);
  ctx.lineTo(ox + hw, oy - H);
  ctx.lineTo(ox, oy - H + hd);
  ctx.lineTo(ox - hw, oy - H);
  ctx.closePath();
  ctx.fill();
  rect(ctx, '#E0689A', ox - 15, oy - H - 3, 12, 4);
  rect(ctx, '#3FA9E8', ox - 2, oy - H - 1, 12, 4);
  rect(ctx, '#E8B53F', ox - 9, oy - H - 6, 10, 4);
}

/** Máquina industrial pixel (produção). (ox,oy) = centro do tile; `acc` = tela. */
export function drawMachine(ctx: Ctx, ox0: number, oy0: number, acc: string) {
  const ox = Math.round(ox0);
  const oy = Math.round(oy0);
  const hw = 14;
  const hd = 7;
  const H = 20;
  ctx.fillStyle = 'rgba(20,40,70,0.20)';
  ctx.beginPath();
  ctx.ellipse(ox, oy + 1, hw, hd * 0.9, 0, 0, 7);
  ctx.fill();
  ctx.fillStyle = '#8C97A6';
  ctx.beginPath();
  ctx.moveTo(ox - hw, oy - H);
  ctx.lineTo(ox, oy - H + hd);
  ctx.lineTo(ox, oy + hd);
  ctx.lineTo(ox - hw, oy);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#76808E';
  ctx.beginPath();
  ctx.moveTo(ox + hw, oy - H);
  ctx.lineTo(ox, oy - H + hd);
  ctx.lineTo(ox, oy + hd);
  ctx.lineTo(ox + hw, oy);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#A6B0BD';
  ctx.beginPath();
  ctx.moveTo(ox, oy - H - hd);
  ctx.lineTo(ox + hw, oy - H);
  ctx.lineTo(ox, oy - H + hd);
  ctx.lineTo(ox - hw, oy - H);
  ctx.closePath();
  ctx.fill();
  rect(ctx, '#2B3340', ox - 11, oy - H + 3, 9, 8);
  rect(ctx, acc, ox - 10, oy - H + 4, 7, 4);
  rect(ctx, '#E04848', ox - 11, oy - 4, 2, 2);
  rect(ctx, '#46C18A', ox - 7, oy - 4, 2, 2);
  rect(ctx, '#E8B53F', ox + 3, oy - H + 3, 8, 3);
  rect(ctx, '#2B2B2B', ox + 3, oy - H + 3, 2, 3);
  rect(ctx, '#2B2B2B', ox + 7, oy - H + 3, 2, 3);
  rect(ctx, '#5A6470', ox - 2, oy - H - hd - 6, 4, 7);
  rect(ctx, '#C8CED6', ox - 3, oy - H - hd - 8, 6, 2);
}

// helpers de caixa iso (faces) + sombra
const shadow = (ctx: Ctx, ox: number, oy: number, rx: number) => {
  ctx.fillStyle = 'rgba(20,40,70,0.20)';
  ctx.beginPath();
  ctx.ellipse(ox, oy + 1, rx, rx * 0.45, 0, 0, 7);
  ctx.fill();
};
const isobox = (
  ctx: Ctx,
  ox: number,
  oy: number,
  hw: number,
  hd: number,
  H: number,
  cl: string,
  cr: string,
  ct: string,
) => {
  ctx.fillStyle = cl;
  ctx.beginPath();
  ctx.moveTo(ox - hw, oy - H);
  ctx.lineTo(ox, oy - H + hd);
  ctx.lineTo(ox, oy + hd);
  ctx.lineTo(ox - hw, oy);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = cr;
  ctx.beginPath();
  ctx.moveTo(ox + hw, oy - H);
  ctx.lineTo(ox, oy - H + hd);
  ctx.lineTo(ox, oy + hd);
  ctx.lineTo(ox + hw, oy);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = ct;
  ctx.beginPath();
  ctx.moveTo(ox, oy - H - hd);
  ctx.lineTo(ox + hw, oy - H);
  ctx.lineTo(ox, oy - H + hd);
  ctx.lineTo(ox - hw, oy - H);
  ctx.closePath();
  ctx.fill();
};

/** Balcão (vendas/caixa/atendimento/bar). (ox,oy)=centro do tile; acc=POS/faixa. */
export function drawCounter(ctx: Ctx, ox0: number, oy0: number, acc: string) {
  const ox = Math.round(ox0);
  const oy = Math.round(oy0);
  shadow(ctx, ox, oy, 15);
  isobox(ctx, ox, oy, 15, 7, 13, '#B98A57', '#9A6637', '#E8C892');
  rect(ctx, acc, ox - 13, oy - 2, 11, 3);
  rect(ctx, '#3A4452', ox + 1, oy - 19, 9, 7);
  rect(ctx, acc, ox + 2, oy - 18, 7, 3);
  rect(ctx, 'rgba(255,255,255,0.2)', ox + 2, oy - 18, 7, 1);
}

/** Fogão/bancada (cozinha). (ox,oy)=centro do tile. */
export function drawStove(ctx: Ctx, ox0: number, oy0: number) {
  const ox = Math.round(ox0);
  const oy = Math.round(oy0);
  shadow(ctx, ox, oy, 13);
  isobox(ctx, ox, oy, 13, 6, 15, '#A8AEB6', '#8C929B', '#C6CBD2');
  ctx.fillStyle = '#2B3036';
  [
    [-6, -15],
    [3, -13],
    [-3, -19],
    [6, -17],
  ].forEach(([dx, dy]) => {
    ctx.beginPath();
    ctx.ellipse(ox + dx, oy + dy, 3, 1.6, 0, 0, 7);
    ctx.fill();
  });
  rect(ctx, '#2B3340', ox - 10, oy - 9, 11, 6);
  rect(ctx, '#C8CED6', ox - 9, oy - 7, 9, 1);
  rect(ctx, '#E04848', ox - 8, oy - 13, 2, 2);
  rect(ctx, '#46C18A', ox - 4, oy - 13, 2, 2);
}

/** Prateleira (logística/estoque). (ox,oy)=centro do tile. */
export function drawShelf(ctx: Ctx, ox0: number, oy0: number) {
  const ox = Math.round(ox0);
  const oy = Math.round(oy0);
  shadow(ctx, ox, oy, 14);
  isobox(ctx, ox, oy, 14, 7, 22, '#9A7B52', '#7E6240', '#B89568');
  rect(ctx, '#E0922A', ox - 11, oy - 9, 7, 6);
  rect(ctx, '#3FA9E8', ox - 2, oy - 7, 7, 6);
  rect(ctx, '#46C18A', ox - 9, oy - 19, 6, 6);
  rect(ctx, '#E0689A', ox + 0, oy - 18, 6, 6);
  rect(ctx, 'rgba(0,0,0,0.12)', ox - 12, oy - 12, 24, 2);
}

/** Maca (saúde). (ox,oy)=centro do tile. */
export function drawMaca(ctx: Ctx, ox0: number, oy0: number) {
  const ox = Math.round(ox0);
  const oy = Math.round(oy0);
  shadow(ctx, ox, oy, 16);
  rect(ctx, '#9AA6B4', ox - 12, oy - 9, 2, 9);
  rect(ctx, '#9AA6B4', ox + 10, oy - 9, 2, 9);
  rect(ctx, '#9AA6B4', ox - 2, oy - 7, 2, 9);
  isobox(ctx, ox, oy - 9, 16, 8, 5, '#BFD6E8', '#A7C2D8', '#E2EEF6');
  rect(ctx, '#FFFFFF', ox - 13, oy - 22, 8, 5);
}

/** Planta pixel. (ox,oy) = centro do tile. */
export function drawPlant(ctx: Ctx, ox0: number, oy0: number) {
  const ox = Math.round(ox0);
  const oy = Math.round(oy0);
  ctx.fillStyle = 'rgba(20,40,70,0.20)';
  ctx.beginPath();
  ctx.ellipse(ox, oy + 1, 7, 3, 0, 0, 7);
  ctx.fill();
  rect(ctx, '#B5651D', ox - 4, oy - 7, 8, 7);
  rect(ctx, '#8C4A12', ox + 0, oy - 7, 4, 7);
  rect(ctx, '#2F9E55', ox - 6, oy - 15, 12, 9);
  rect(ctx, '#37B463', ox - 4, oy - 19, 8, 6);
  rect(ctx, '#268A49', ox + 1, oy - 13, 5, 6);
}

/** Balão de emoji (status) acima do avatar. (ox,oy) = pés; sobe ~46px. */
export function drawBubble(ctx: Ctx, ox0: number, oy0: number, emoji: string) {
  const ox = Math.round(ox0);
  const oy = Math.round(oy0) - 46;
  ctx.fillStyle = '#fff';
  ctx.strokeStyle = 'rgba(40,70,110,0.25)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.roundRect(ox - 10, oy - 10, 20, 17, 5);
  ctx.fill();
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(ox - 3, oy + 6);
  ctx.lineTo(ox + 3, oy + 6);
  ctx.lineTo(ox, oy + 11);
  ctx.closePath();
  ctx.fillStyle = '#fff';
  ctx.fill();
  ctx.font = '12px sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = '#000';
  ctx.fillText(emoji, ox, oy - 1);
}
